<template>
    <loading-icon v-if="loading && !error" />
    <error-panel v-if="!loading && error" :message="error" />
    <div class="mt-3" v-if="!loading && !error">
        <div class="container container-print-fluid">
            <div class="row" v-if="!loading">
                <div class="col-print-full col-10 offset-1">
                    <p>
                        Congratulations on making evacuation readiness a priority at your facility. This data-driven assessment helps
                        identify the amount of equipment needed to evaluate your facility in a single flow<sup>&dagger;</sup>.
                        <span class="d-print-none">
                            We'll e-mail you a copy of your results to {{info.emailAddress}}, and you can
                            also <a :href="`/report/${$route.params.responseId}`" target="_new">download a copy of your results</a>.
                        </span>
                    </p>
                    <hr />
                    <h3 class="text-center m-0">{{info.organizationName}}</h3>
                    <h4 class="text-center">Single-Flow Evacuation Readiness Assessment Results</h4>
                    <div class="row">
                        <div class="col-3 text-end"><strong>Results Generated By:</strong></div>
                        <div class="col-3">{{info.contactName}}</div>
                        <div class="col-3 text-end"><strong>Average Daily Census:</strong></div>
                        <div class="col-3">{{info.averageDailyCensus}}</div>
                    </div>
                    <div class="row">
                        <div class="col-3 text-end"><strong>State:</strong></div>
                        <div class="col-3">{{info.state}}</div>
                        <div class="col-3 text-end"><strong>Non-Ambulatory Percentage:</strong></div>
                        <div class="col-3">{{info.nonAmbulatoryRatePercentage}}</div>
                    </div>
                    <div class="row">
                        <div class="col-3 text-end"><strong>Date:</strong></div>
                        <div class="col-3">{{info.nowDate}}</div>
                        <div class="col-3 text-end"><strong>Current Equipment Count:</strong></div>
                        <div class="col-3">{{info.currentEvaluationEquipment}}</div>
                    </div>
                    <hr />
                    <h3 class="text-center">SINGLE-FLOW EVACUATION READINESS LEVELS</h3>
                    <p>
                        Readiness levels are based on being equipped to evacuate your facility in a single flow. What is single flow
                        evacution? The ability to evacuate your entire non-ambulatory and at-risk population from a compromised facility
                        in a single operation without re-entry. <span class="d-print-none">Learn more <a href="https://www.ethospreparedness.com/03-09-22-single-flow-evacuation.html" target="_new">here</a>.</span>
                    </p>
                </div>
            </div>

            <div class="row text-center" style="color: white; font-weight: bold;">
                <div class="col py-2" :class="{'opacity-50': info.prepColorRating !== 'red'}" style="background: linear-gradient(90deg, #e52204 50%, #ff9900 150%);">RED</div>
                <div class="col py-2" :class="{'opacity-50': info.prepColorRating !== 'orange'}" style="background: linear-gradient(90deg, #e52204 -50%, #ff9900 50%, #f0d500 150%);">ORANGE</div>
                <div class="col py-2" :class="{'opacity-50': info.prepColorRating !== 'yellow'}" style="background: linear-gradient(90deg, #ff9900 -50%, #f0d500 50%, #01b013 150%);">YELLOW</div>
                <div class="col py-2" :class="{'opacity-50': info.prepColorRating !== 'green'}" style="background: linear-gradient(90deg, #f0d500 -50%, #01b013 50%);">GREEN</div>
            </div>
            <div class="row text-center fw-bold">
                <div class="col py-2" :class="{'opacity-50': info.prepColorRating !== 'red'}">0&ndash;25% Equipped</div>
                <div class="col py-2" :class="{'opacity-50': info.prepColorRating !== 'orange'}">26&ndash;50% Equipped</div>
                <div class="col py-2" :class="{'opacity-50': info.prepColorRating !== 'yellow'}">51&ndash;75% Equipped</div>
                <div class="col py-2" :class="{'opacity-50': info.prepColorRating !== 'green'}">75%+ Equipped</div>
            </div>

            <div class="row mt-3">
                <div class="col-print-full col-10 offset-1">

                    <div class="row">
                        <div class="col-10">
                            <h3>YOUR READINESS LEVEL: <span class="readiness" :class="`readiness-${info.prepColorRating}`">{{info.prepColorRating}}</span></h3>
                            <div v-if="info.prepColorRating === 'red'">
                                <p>Whether you have been at this level of readiness for several years or are just getting started, it is time to 
                                    consider being better prepared for a major event at your facility. <strong>The evacuation process would be prolonged, involve 
                                    re-use of equipment, and may require methods that could put your patients + staff at risk.</strong></p>
                    
                                <p>Your current evacuation equipment:</p>
                                <ul>
                                    <li>Will require staff or first responders to make return trips to re-use equipment at a very high rate</li>
                                    <li>May not meet the specialty needs of your non-ambulatory population</li>
                                    <li>Does not support a single-flow evacuation plan</li>
                                </ul>
                            </div>
                            <div v-if="info.prepColorRating === 'orange'">
                                <p>You are making good progress, but still have some work to do. Evacuating a single unit or section of the facility would be more 
                                    manageable, depending on your census, but a major event would require re-use of equipment.</p>
                    
                                <p>Your current evacuation equipment:</p>
                                <ul>
                                    <li>Will likely require staff or first responders to make return trips to re-use equipment at a high rate</li>
                                    <li>May not meet the specialty needs of your non-ambulatory population</li>
                                    <li>Does not support a single-flow evacuation plan</li>
                                </ul>
                            </div>
                            <div v-if="info.prepColorRating === 'yellow'">
                                <p>You are over half-way home to being fully equipped! Time to evaluate any specialty equipment needs to 
                                    supplement your existing inventory. Having a strategic plan for deployment is key too.</p>
                    
                                <p>Your current evacuation equipment:</p>
                                <ul>
                                    <li>Likely only supports the full evacuation of a limited area of your facility</li>
                                    <li>May not meet the specialty needs of your non-ambulatory population like NICU or Bariatric</li>
                                    <li>Does not support a single-flow evacuation plan</li>
                                </ul>
                            </div>
                            <div v-if="info.prepColorRating === 'green'">
                                <p>You have secured enough or almost enough equipment to move everyone needing assistance out of the building in a single flow. 
                                    Equipment deployment, storage, and training for your staff will have you fully prepared.</p>
                    
                                <p>At this level you should focus your efforts on:</p>
                                <ul>
                                    <li>Training and deployment</li>
                                    <li>Storage options that meet the needs of your aesthetics committee</li>
                                </ul>
                            </div>
                            <div class="col py-1" id="equip" style="background-color: #494F56; color: white;">
                                <div class="row">
                                    <div class="col-4 fw-bold text-end">Current Equipment:</div>
                                    <div class="col-2">{{info.currentEvaluationEquipment}}</div>
                                    <div class="col-4 fw-bold text-end">Remaining Equipment Need:</div>
                                    <div class="col-2">{{info.equipmentNeeded}}</div>
                                </div>
                            </div>
                        </div>
                        <div class="col-2">
                            <svg id="svg" width="150" height="150" viewport="0 0 150 150" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                <circle class="track" r="60" cx="75" cy="75"></circle>
                                <circle class="bar" :class="`bar-${info.prepColorRating}`" r="60" cx="75" cy="75" :stroke-dashoffset="Math.floor((info.prepRatio) * 377)"></circle>
                                <text x="75" y="75">{{Math.floor((1-info.prepRatio)*100)}}%</text>
                            </svg>
                            
                        </div>
                    </div>


            <p class="small mt-3">&dagger; Please note that your results are based on the facility data provided and the protocol of being 100% equipped,
                which is having a single piece of equipment for every non-ambulatory or at-risk person at your facility. Being
                100% equipped allows for a single flow evacuation. Although not mandatory by any regulatory agency, real life
                events have shown this as a best practice in evacuation events.</p>
                <hr />
            <h4 class="text-center">Next Steps:</h4>
            <p>To get to the next level in evacuation readiness we recommend you familiarize yourself with the full suite of
                patient evacuation equipment (NICU, Youth, Bariatric) and get in touch with your regional manager for a more in
                depth facility evaluation.</p>
            <p>We'll help match your patient makeup to exact equipment recommendations along with training &amp; storage
                options. Remember, achieving single-flow evacuation readiness is often a multi-step process that we are able to
                assist with.</p>
            <div class="row d-print-none" id="sled-deck">
                <div class="col">
                    <img src="/img/1.png" />
                    <div class="my-2"><strong>Multi-Sized Evacuation Sleds</strong></div>
                </div>
                <div class="col">
                    <img src="/img/2.png" />
                    <div class="my-2"><strong>NICU Evacuation Systems</strong></div>
                </div>
                <div class="col">
                    <img src="/img/3.png" />
                    <div class="my-2"><strong>DECON &amp; Specialty Evacuation</strong></div>
                </div>
                <div class="col">
                    <img src="/img/4.png" />
                    <div class="my-2"><strong>Evacuation Chairs</strong></div>
                </div>
            </div>
            <div class="row d-print-none">
                <div class="col-3 offset-3">
                    <a href="https://www.ethospreparedness.com/organizations/evacuation-equipment.html" target="_new" class="btn btn-primary" style="width: 100%">Browse Equipment</a>
                </div>
                <div class="col-3">
                    <a href="https://www.ethospreparedness.com/organizations/contact-us.html" target="_new" class="btn btn-primary" style="width: 100%">Contact Regional Manager</a>
                </div>
            </div>
            <div class="row my-4 d-print-none">
            </div>
        </div>
        </div>
        </div>
    </div>
</template>

<script>
import ErrorPanel from './ErrorPanel.vue';
import LoadingIcon from './LoadingIcon.vue';

export default {
    name: 'Results',
    components: {
        LoadingIcon,
        ErrorPanel
    },
    async mounted() {
        const rval = await fetch(`/api/ethos?responseId=${this.$route.params.responseId}`, {
            method: 'POST',
            body: '',
        });
        if (rval.status !== 200) {
            this.error = 'Could not retrieve assessment information!';
        }
        else {
            const json = await rval.json();
            this.info = json;
        }
        this.loading = false;
    },
    data() {
        return {
            loading: true,
            error: null,
            info: {
                nowDate: '',
                organizationName: '',
                contactName: '',
                averageDailyCensus: 0,
                state: '',
                currentEvaluationEquipment: 0,
                nonAmbulatoryRatePercentage: 0,
                equipmentNeeded: 0,
                prepRatio: 0,
                prepColorRating: 'red',
                emailAddress: '',
            },
        };
    },
};
</script>